<template>
  <div class="template-editor-wrapper">
    <a-collapse
        v-model:model="showInstruction"
        expandIconPosition="end"
        :bordered="false"
        style="font-weight: 600"
    >
      <a-collapse-panel key="1" header="Как работать с шаблонами писем?" style="width: 100%; font-weight: 400; padding: 16px">
        <div class="alert-block">
          <p>
            <span class="semi-bold">Визуальный редактор</span> &ndash; с его помощью вы можете: выбирать шрифт, настраивать его размер, выделять важное цветом, добавлять списки и таблицы, изображения и внешние ссылки.
          </p>
          <p>
            <span class="semi-bold">Переменные</span> &ndash; в выпадающем списке выберите переменную <span class="semi-bold">(Имя сотрудника, Имя HR)</span>, после чего она добавится в текст в формате <span class="semi-bold">[Employee Name], [HR Name]</span>.
          </p>
        </div>

        <div class="example-block">
          <a-card>
            <div class="template-example">
              <div class="example-title">
                Пример:
              </div>

              <p>Уважаемый <span class="mail-variable">[Employee_Name]</span>!</p>
              <p>
                Компания <span class="mail-variable">[Company]</span> приглашает вас пройти
                оценку эмоционального интеллекта на портале WAY2WEI.
              </p>
              <p>
                Пожалуйста, перейдите по ссылке ниже для начала прохождения.<br>
                <span class="mail-variable mail-variable-link">[Test_Link]</span>
              </p>
              <br>
              <p>
                С уважением,<br>
                <span class="mail-variable">[HR_Name]</span>
              </p>
            </div>
          </a-card>
        </div>

        <a-alert show-icon message="Не меняйте и не удаляйте скобки и текст внутри них, иначе данные не подставятся в письме." type="warning">
          <template #icon>
            <InfoCircleFilled style="color: var(--sunset-orange)"/>
          </template>
        </a-alert>
      </a-collapse-panel>
    </a-collapse>

    <template v-if="!templatesLoading">
      <div v-for="(value, key) in templatesForm" :key="key">
        <div class="template-title">{{ value.name }}</div>
        <MailEditor
            v-model:content="templatesForm[key].data"
            :type="templatesForm[key].type"
            :variables="defaultVariables"
            @reset="deleteTemplate(key)"
            @update:content="onTemplateChange(key, $event)"
        />
      </div>
    </template>
    <div v-else class="spinner">
      <a-spin />
    </div>
  </div>
</template>

<script>
  import MailEditor from "@/components/Notification/Editor.vue";
  import {onBeforeMount, ref} from "@vue/runtime-core";
  import {useStore} from "vuex";
  import {computed, reactive} from "@vue/reactivity";
  import {InfoCircleFilled} from "@ant-design/icons-vue";
  import {message} from "ant-design-vue";

  export default {
    components: {InfoCircleFilled, MailEditor},
    setup(props, {expose}) {
      const store = useStore();
      const userTemplates = computed(() => store.state.notifications.customTemplates);
      const profile = computed(() => store.state.profile.profile);
      const templatesLoading = ref(false);
      const showInstruction = ref(false);

      const defaultTemplateOptions = {
        start_passing_test: "<p>Здравствуйте!</p><p>В личном кабинете на платформе Way2Wei вам доступно прохождение теста на Эмоциональный Интеллект.</p><span class=\"mail-variable-link\">[Test_Link]</span><br /><p>Ранее вам приходило письмо с логином и паролем (тема письма «Регистрация в личном кабинете Way2Wei»).</p>",
        continue_passing_test: "<p>Здравствуйте!</p><p>Вы не завершили прохождение теста на Эмоциональный Интеллект!</p><span class=\"mail-variable-link\">[Test_Link]</span>",
        start_passing_course: "<p>Здравствуйте!</p><p>В личном кабинете на платформе Way2Wei вам доступен курс <span class=\"mail-variable\">[Course_Name]</span>.</p><span class=\"mail-variable-link\">[Course_Link]</span><br />",
        continue_passing_course: "<p>Здравствуйте!</p><p>Вы не завершили прохождение курса <span class=\"mail-variable\">[Course_Name]</span>!</p><span class=\"mail-variable-link\">[Course_Link]</span>"
      }

      const defaultVariables = [
        { label:'Имя сотрудника', value:'Employee_Name', text:'[Employee_Name]', type:'text' },
        { label:'Имя HR', value:'HR_Name', text:'[HR_Name]', type:'text' },
        { label:'Название теста', value:'Test_Name', text:'[Test_Name]', type:'text' },
        { label:'Ссылка на тест', value:'Test_Link', text:'[Test_Link]', type:'link' },
        { label:'Название курса', value:'Course_Name', text:'[Course_Name]', type:'text' },
        { label:'Ссылка на курс', value:'Course_Link', text:'[Course_Link]', type:'link' },
        { label:'Компания', value:'Company', text:'[Company]', type:'text' }
      ];

      const templatesForm = reactive({
        start_passing_test: {
          name: 'Новый тест доступен',
          type: 'default',
          data: defaultTemplateOptions.start_passing_test,
        },
        continue_passing_test: {
          name: 'Напоминание о непройденном тесте',
          type: 'default',
          data: defaultTemplateOptions.continue_passing_test,
        },
        start_passing_course: {
          name: 'Новый курс доступен',
          type: 'default',
          data: defaultTemplateOptions.start_passing_course,
        },
        continue_passing_course: {
          name: 'Напоминание о прохождение курса',
          type: 'default',
          data: defaultTemplateOptions.continue_passing_course,
        }
      });

      onBeforeMount(async () => {
        templatesLoading.value = true;
        await store.dispatch('notifications/getAvailableParams');
        await fetchCustomTemplates();
      });

      const fetchCustomTemplates = async () => {
        templatesLoading.value = true;

        await store.dispatch('notifications/getCustomTemplates').then(() => {
          userTemplates.value.forEach((el) => {
            templatesForm[el.notification_key].data = wrapTemplateVariables(el.body);
            templatesForm[el.notification_key].type = 'custom';
          });
        }).finally(() => {
          templatesLoading.value = false;
        });
      }

      const saveTemplates = async () => {
        let templateChanged = false;

        for (const key of Object.keys(templatesForm)) {
          if (templatesForm[key].type === 'default') continue;

          templateChanged = true;

          if (checkTemplateExists(key)) {
            const templateId = userTemplates.value.find(
                (el) => el.notification_key === key
            ).id;

            await store.dispatch('notifications/updateTemplate', {
              id: templateId,
              body: cleanTemplateHtml(templatesForm[key].data),
              user_id: profile.value.id,
            });
          } else {
            await store.dispatch('notifications/createTemplate', {
              notification_key: key,
              body: cleanTemplateHtml(templatesForm[key].data),
              user_id: profile.value.id,
            });
          }
        }

        if (!templateChanged) {
          message.info('Пожалуйста, внесите изменения в шаблоны');
          return;
        }

        message.success('Шаблоны обновлены');
        await fetchCustomTemplates();
      };

      const deleteTemplate = (template) => {
        store.dispatch('notifications/deleteCustomTemplate', {
          id: userTemplates.value.find((el) => el.notification_key === template).id
        }).then(() => {
          templatesForm[template].data = defaultTemplateOptions[template];
          templatesForm[template].type = 'default';
          message.success('Шаблон восстановлен');
          fetchCustomTemplates();
        });
      }

      const checkTemplateExists = (type) => {
        return userTemplates.value.find((el) => el.notification_key === type);
      }

      const onTemplateChange = (key, value) => {
        templatesForm[key].data = value
        templatesForm[key].type =
            value === defaultTemplateOptions[key] ? 'default' : 'custom'
      }

      const cleanTemplateHtml = (html = '') => {
        return html.replace(
            /<span\s+class="[^"]*\bmail-variable\b[^"]*">([\s\S]*?)<\/span>/gi,
            '$1'
        );
      }

      const wrapTemplateVariables = (html = '') => {
        let result = cleanTemplateHtml(html);

        defaultVariables.forEach((variable) => {
          const className =
              variable.type === 'link'
                  ? 'mail-variable mail-variable-link'
                  : 'mail-variable'

          const escapedToken = variable.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

          result = result.replace(
              new RegExp(escapedToken, 'g'),
              `<span class="${className}">${variable.text}</span>`
          )
        });

        return result;
      }

      expose({
        saveTemplates
      });

      return {
        showInstruction,
        templatesForm,
        deleteTemplate,
        onTemplateChange,
        templatesLoading,
        defaultVariables
      }
    }
  }
</script>

<style scoped lang="scss">
  .template-editor-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;

    :deep(.ant-collapse-header) {
      padding: 0 !important;
    }

    :deep(.ant-collapse-content-box) {
      padding: 16px 0 !important;
    }
  }

  .template-title {
    margin-bottom: 12px;
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
  }

  .example-block {
    margin-bottom: 16px;
  }

  .spinner {
    display: flex;
    justify-content: center;
    margin-top: 20%;

    @media (max-width: $mobile-breakpoint) {
      margin-top: 10%;
    }
  }

  .template-example {
    p {
      margin-bottom: 0;
      font-size: 14px;
      line-height: 120%;
    }

    .example-title {
      margin-bottom: 10px;
      font-weight: 600;
    }

    .mail-variable {
      font-weight: 500;
    }

    .mail-variable-link {
      color: var(--main-green);
    }
  }
</style>